import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import './Header.css';
import logo from '../images/critical-river_logo.png';
import { useLayoutEffect } from 'react';
import fallback from '../images/fallback.png';
import { apiFetch } from '../utils/api';
import { handleUrlParams } from '../utils/urlParams';

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAdmin, setIsAdmin] = useState(false);
  const [isManager, setIsManager] = useState(false);
  // Use white placeholder data URL while loading
  const whitePlaceholder = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiBmaWxsPSIjRkZGRkYiLz48L3N2Zz4=';
  const [profilePicUrl, setProfilePicUrl] = useState(whitePlaceholder);
  const [imageLoaded, setImageLoaded] = useState(false);
  const dropdownRef = useRef(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Universal scroll-to-top on route change
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);


  useEffect(() => {
    // Set all items from URL params and clean up URL
    handleUrlParams();

    const initializeUserData = async () => {
      const userEmail = localStorage.getItem('userEmail');

      if (!userEmail) {
        console.error("No userEmail found in localStorage");
        setIsAdmin(false);
        setIsManager(false);
        return;
      }

      try {
        // Fetch user info for admin/manager status
        const userInfo = await apiFetch(`${window._env_.API_BASE_URL}/users/info?email=${userEmail}`);
        setIsAdmin(userInfo.is_admin === true);
        setIsManager(userInfo.isManager === true);

        // Fetch profile picture directly from Azure AD via backend proxy
        const photoUrl = `${window._env_.API_BASE_URL}/profileimg/get-azure-ad-photo?email=${userEmail}`;
        setProfilePicUrl(photoUrl);
      } catch (err) {
        console.error('Failed to fetch user info:', err);
        setIsAdmin(false);
        setIsManager(false);

        // Fallback still uses the Azure AD proxy
        const photoUrl = `${window._env_.API_BASE_URL}/profileimg/get-azure-ad-photo?email=${userEmail}`;
        setProfilePicUrl(photoUrl);
      }
    };

    initializeUserData();
  }, [location.search]);


  // Profile dropdown handlers
  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleProfileClick = () => {
    setIsDropdownOpen(false);
    navigate('/profile');
  };

  const handleLogout = async () => {
    try {
      localStorage.clear();
      sessionStorage.clear();
      setIsDropdownOpen(false);
      const logoutUrl = window._env_?.API_LOGOUT;
      window.location.href = logoutUrl;
    } catch (e) {
      console.error('Error during logout:', e);
    }
  };

  // Close dropdown when clicking outside and reposition if needed
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen && dropdownRef.current) {
      const dropdownMenu = dropdownRef.current.querySelector('.dropdown-menu');
      if (dropdownMenu) {
        dropdownMenu.style.right = '';
        dropdownMenu.style.left = '';
        const rect = dropdownMenu.getBoundingClientRect();
        if (rect.right > window.innerWidth) {
          dropdownMenu.style.right = '0';
          dropdownMenu.style.left = 'auto';
        }
        if (rect.left < 0) {
          dropdownMenu.style.left = '0';
          dropdownMenu.style.right = 'auto';
        }
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  return (
    <header className="header">
      <div className="header-left">
        <img src={logo} alt="CriticalRiver Logo" className="logo" />
      </div>
      <nav className="header-center">
        <NavLink to="/Home" className={({ isActive }) => (isActive ? 'active-link' : '')}>
          Home
        </NavLink>
        <NavLink to="/my-courses" className={({ isActive }) => (isActive ? 'active-link' : '')}>
          My Courses
        </NavLink>
        <NavLink to="/departments" className={({ isActive }) => (isActive ? 'active-link' : '')}>
          Departments
        </NavLink>
        <NavLink to="/scorm" className={({ isActive }) => (isActive ? 'active-link' : '')}>
          SCORM
        </NavLink>
        <NavLink to="/certificates" className={({ isActive }) => (isActive ? 'active-link' : '')}>
          Certificates
        </NavLink>
        {isManager && (
          <NavLink to="/manage-learning-paths" className={({ isActive }) => (isActive ? 'active-link' : '')}>
            Manage Learning Paths
          </NavLink>
        )}
        {isAdmin && (
          <NavLink to="/admin" className={({ isActive }) => (isActive ? 'active-link' : '')}>
            Admin
          </NavLink>
        )}
      </nav>
      <div className="header-right">
        <div className="user-profile" ref={dropdownRef}>
          <img
            src={profilePicUrl}
            alt="Profile"
            className="profile-pic"
            onClick={toggleDropdown}
            onLoad={() => setImageLoaded(true)}
            onError={(e) => {
              console.error('Failed to load profile picture:', {
                url: e.target.src,
                error: e.type,
                timestamp: new Date().toISOString()
              });

              // Don't try to refresh if we're already showing fallback or white placeholder
              if (e.target.src === fallback || e.target.src === whitePlaceholder) {
                return;
              }

              // Fallback to fallback image on error
              e.target.src = fallback;
              setProfilePicUrl(fallback);
            }}
            style={{
              backgroundColor: '#FFF',
              display: 'block'
            }}
          />
          {isDropdownOpen && (
            <div className="dropdown-menu">
              <div className="dropdown-item" onClick={handleProfileClick}>
                Profile
              </div>
              <div className="dropdown-item" onClick={handleLogout}>
                Logout
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;